import React, { useState } from "react";
import SortableList, { DragHandle } from "../../ui/SortableList";
import SubTaskForm from "../forms/SubTaskForm";
import Button from "../../ui/Button";

/**
 * Reusable SubTask list editor with inline editing
 * Manages array of subtasks with drag-and-drop reordering
 */
const SubTaskListEditor = ({
  subTasks = [],
  onChange,
  mode = "project",
  showSaveToRegistry = false,
  onAddFromRegistry,
  disabled = false,
  defaultBillingModel = ""
}) => {
  const [editingId, setEditingId] = useState(null);

  const handleAddNew = () => {
    const newSubTask = {
      id: `temp-${Date.now()}`,
      name: "",
      description: "",
      weight: 1,
      estimatedHours: "",
      billingModel: defaultBillingModel || "",
      hourlyRate: "",
      fixedPrice: "",
      isNew: true
    };
    onChange([...subTasks, newSubTask]);
    setEditingId(newSubTask.id);
  };

  const handleUpdate = (id, updatedSubTask) => {
    onChange(subTasks.map(st => st.id === id ? { ...st, ...updatedSubTask } : st));
  };

  const handleSave = (id) => {
    const subTask = subTasks.find(st => st.id === id);
    if (subTask && !subTask.name?.trim()) {
      alert("Subtask name is required");
      return;
    }
    setEditingId(null);
  };

  const handleCancel = (id) => {
    const subTask = subTasks.find(st => st.id === id);
    if (subTask?.isNew) {
      // Remove if it's a new unsaved subtask
      onChange(subTasks.filter(st => st.id !== id));
    }
    setEditingId(null);
  };

  const handleRemove = (id) => {
    if (window.confirm("Remove this subtask?")) {
      onChange(subTasks.filter(st => st.id !== id));
      if (editingId === id) {
        setEditingId(null);
      }
    }
  };

  const handleReorder = (reorderedItems) => {
    const reordered = reorderedItems.map((item, index) => ({
      ...item.subTask,
      sortOrder: index
    }));
    onChange(reordered);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium">
          Subtasks ({subTasks.length})
        </label>
        <div className="flex gap-2">
          {onAddFromRegistry && (
            <button
              type="button"
              onClick={onAddFromRegistry}
              className="text-sm text-blue-600 hover:underline"
              disabled={disabled}
            >
              + From Registry
            </button>
          )}
          <button
            type="button"
            onClick={handleAddNew}
            className="text-sm text-blue-600 hover:underline"
            disabled={disabled}
          >
            + New Subtask
          </button>
        </div>
      </div>

      {subTasks.length === 0 ? (
        <div className="border rounded p-4 text-center text-sm text-gray-500">
          No subtasks yet. Add one to get started.
        </div>
      ) : (
        <SortableList
          items={subTasks.map((subTask) => ({
            id: subTask.id || subTask._id,
            subTask
          }))}
          onReorder={handleReorder}
          renderItem={(item, dragHandleProps) => {
            const isEditing = editingId === item.id;
            const subTask = item.subTask;

            return (
              <div className="bg-white border rounded p-3">
                {isEditing ? (
                  // Inline editing form
                  <div className="space-y-3">
                    <SubTaskForm
                      value={subTask}
                      onChange={(updated) => handleUpdate(item.id, updated)}
                      mode={mode}
                      showSaveToRegistry={showSaveToRegistry}
                      disabled={disabled}
                      defaultBillingModel={defaultBillingModel}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleCancel(item.id)}
                        size="sm"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="solid"
                        onClick={() => handleSave(item.id)}
                        size="sm"
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Collapsed view
                  <div className="flex items-center gap-2">
                    <DragHandle listeners={dragHandleProps} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">
                        {subTask.name || "(Unnamed)"}
                      </div>
                      {subTask.description && (
                        <div className="text-xs text-gray-500 truncate">
                          {subTask.description}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        {subTask.weight && `Weight: ${subTask.weight}`}
                        {subTask.weight && subTask.estimatedHours && " • "}
                        {subTask.estimatedHours && `${subTask.estimatedHours}h`}
                        {(subTask.weight || subTask.estimatedHours) && (subTask.billingModel || defaultBillingModel) && " • "}
                        {(subTask.billingModel || defaultBillingModel) && `Billing: ${subTask.billingModel || defaultBillingModel}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditingId(item.id)}
                      className="text-sm text-blue-600 hover:underline px-2"
                      disabled={disabled}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemove(item.id)}
                      className="text-red-600 hover:text-red-800 px-2"
                      disabled={disabled}
                      title="Remove subtask"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            );
          }}
        />
      )}
    </div>
  );
};

export default SubTaskListEditor;
